import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type { HypeMember, SessionResponse } from '@movienight/shared';
import { BulbFrame } from './BulbFrame';
import { NightSky } from './NightSky';
import { Board } from './Board';
import { LangSign } from './LangSign';
import { ROUTES, useRoute } from '../lib/route';
import './home.css';

/**
 * What you see once you are through the door: the marquee, the board, and the
 * night sky the two of them stand in.
 *
 * The page is still mostly sky, and that is the design rather than a shortage
 * of content — the site's most common state is "nothing currently scheduled"
 * (DESIGN.md §16a), so the board's empty state is a real designed state and the
 * city behind it carries the page on the quiet weeks.
 */

/**
 * Split out of the main bundle. The panel is reachable by one person, the door
 * doubles as the loading screen, and this is the whole of scheduling — it has
 * no business in the critical path on Serbian mobile data.
 */
const AdminPanel = lazy(() =>
  import('../admin/AdminPanel').then((m) => ({ default: m.AdminPanel })),
);

/**
 * Split out for the same reason, and a **page** rather than an overlay: it is
 * at `#/predlozi`, the phone's Back button leaves it, and the link can be
 * pasted into the group chat. See `lib/route.ts`.
 *
 * It used to be a modal, and the modal was owned by `Board` — which returns
 * early for its own loading and error states, so a failed background refetch
 * unmounted the room and threw away the half-written card in it. Being a route
 * deletes that whole class of bug rather than fixing it: on this page the board
 * is not mounted at all.
 */
const PitchRoom = lazy(() => import('../pitch/PitchRoom').then((m) => ({ default: m.PitchRoom })));

/**
 * The desk, split out with Matter.js behind it (the `physics` chunk in
 * vite.config.ts). Nobody should download a physics engine to see the board.
 */
const Desk = lazy(() => import('../desk/Desk').then((m) => ({ default: m.Desk })));

const TITLES: Record<string, string> = {
  board: 'Filmsko veče',
  pitch: 'Predlozi — Filmsko veče',
  desk: 'Moj sto — Filmsko veče',
};

export function Home({
  session,
  onLogout,
}: {
  session: SessionResponse & { user: NonNullable<SessionResponse['user']> };
  onLogout: () => void;
}) {
  const [admin, setAdmin] = useState(false);
  const route = useRoute();

  /**
   * The two things a page swap owes you that a modal does not: a title, and the
   * top of the page. Arriving at the corkboard halfway down because that is
   * where the board was scrolled is the tell that a "page" is really a div.
   */
  useEffect(() => {
    document.title = TITLES[route] ?? TITLES.board!;
    window.scrollTo({ top: 0 });
  }, [route]);

  /**
   * Who you are, in the shape the hype meter wants. Passed down rather than
   * read from the socket's `hello` frame: the board has to be able to put your
   * face on a card the instant you tap a flame, and that means knowing your
   * nickname, not just your id.
   */
  const me = useMemo<HypeMember>(
    () => ({
      id: session.user.id,
      nickname: session.user.nickname,
      avatarUrl: session.user.avatarUrl,
    }),
    [session.user.id, session.user.nickname, session.user.avatarUrl],
  );

  // The desk is a full-screen view of its own (DESIGN.md §10), so it replaces
  // the night rather than sitting in it: an opaque desk over a sky that keeps
  // twinkling and drifting would be animating for nobody.
  if (route === 'desk') {
    return (
      <Suspense fallback={null}>
        <Desk />
      </Suspense>
    );
  }

  return (
    <div className="night">
      <NightSky />

      <div className="night-content">
        <div className="title-wrap">
          <div className="title-stars" />
          <div className="title-sign">
            <div className="title-deco title-deco--l" />
            <BulbFrame className="title-plaque">
              <h1 className="title-text">FILMSKO VEČE</h1>
            </BulbFrame>
            <div className="title-deco title-deco--r" />
          </div>
          <div className="title-base" />
        </div>

        {route === 'pitch' ? (
          <Suspense fallback={null}>
            <PitchRoom me={me} />
          </Suspense>
        ) : (
          <Board me={me} />
        )}

        {/* Outside the route switch on purpose: the sign is site-wide, and a
            preference control that vanishes on the page where you noticed you
            wanted it is a control nobody finds twice. */}
        <LangSign />

        <div className="night-who">
          <dl className="night-meta">
            <dt>ko</dt>
            <dd>{session.user.nickname}</dd>
            <dt>podmićen</dt>
            <dd>{session.user.bribedGuard ?? '—'}</dd>
            <dt>otkazivanja</dt>
            <dd>{session.lateCancels}</dd>
            {session.user.isAdmin && (
              <>
                <dt>uloga</dt>
                <dd>domaćin</dd>
              </>
            )}
          </dl>

          <div className="night-actions">
            <a className="night-logout" href={ROUTES.desk}>
              moj sto
            </a>
            {session.user.isAdmin && (
              <button type="button" className="night-logout" onClick={() => setAdmin(true)}>
                raspored
              </button>
            )}
            <button type="button" className="night-logout" onClick={onLogout}>
              izađi
            </button>
          </div>
        </div>
      </div>

      {admin && (
        <Suspense fallback={null}>
          <AdminPanel onClose={() => setAdmin(false)} />
        </Suspense>
      )}
    </div>
  );
}
